import { useEffect, useState } from 'react';
import { leaveTypeSchema, overtimeTypeSchema } from '@forecastify/shared';
import { useSession } from '../../session';
import { parseList } from '../permissions-hook';
import type { DecisionOptions } from './decision-controls';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The two lists an overtime decision chooses from.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Read once per workspace rather than per request.** Both are a handful of rows and neither
 * changes while a drawer or a dialog is open, so a read per opened claim would be a request per
 * click for an answer that does not move.
 *
 * ⚠️⚠️ **NOT FATAL, AND THAT IS DELIBERATE.** A reader who cannot list either gets no picker and
 * the server settles the claim the way it always has: a payout, priced from the calendar. A
 * decision surface that refused to open because a secondary list failed would make an unreadable
 * leave type into an unapprovable claim.
 *
 * ⚠️ **One hook, two surfaces** — the detail rail and the row menu's dialog — for the reason
 * `decision-controls.tsx` records: two copies of this drift.
 */
export function useDecisionOptions(): DecisionOptions {
  const session = useSession();
  /**
   * ⚠️⚠️ **WHERE A CREDIT MAY LAND — feedback round 4 item 31, and item 25's other end.** Only the
   * types that draw on a QUOTA: crediting days to an unlimited type would add a number to a balance
   * with no frame to add it to, and the server refuses one anyway.
   *
   * ⚠️ **The workspace's own default first**, which is what makes the product owner's *"a default
   * the approver can change"* true of the control rather than only of the server: the first option
   * is the pre-selected one, and it is the same type `creditTargetFor` would have picked.
   */
  const [creditTypes, setCreditTypes] = useState<
    readonly { readonly id: string; readonly name: string }[]
  >([]);
  /**
   * ⚠️⚠️ **WHAT A PAYOUT MAY BE PRICED AS — `0147`.** The active types in the workspace's own
   * order, which is what puts the usual one first.
   */
  const [overtimeTypes, setOvertimeTypes] = useState<
    readonly { readonly id: string; readonly name: string; readonly multiplier: string }[]
  >([]);

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/workspace/leave/types').catch(() => null);
      if (response?.ok !== true) return;
      const rows = parseList(leaveTypeSchema, await response.json());
      if (rows === null) return;
      setCreditTypes(
        rows
          /* ⚠️ A destination is a type whose quota COMES FROM overtime — `leave.ts` argues why
             that is a quota source rather than a flag. Sort order decides the default. */
          .filter((type) => type.active && type.quotaSource === 'overtime')
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((type) => ({ id: type.id, name: type.name })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId]);

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/overtime/types').catch(() => null);
      if (response?.ok !== true) return;
      const rows = parseList(overtimeTypeSchema, await response.json());
      if (rows === null) return;
      setOvertimeTypes(
        rows
          .filter((type) => type.active)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((type) => ({ id: type.id, name: type.name, multiplier: type.multiplier })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
  }, [session.workspaceId]);

  return { creditTypes, overtimeTypes };
}
